const NEW_YORK_TIME_ZONE = "America/New_York";
const NEW_YORK_DATE_TIME_PARTS_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: NEW_YORK_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});
const NEW_YORK_DATE_TIME_PARTS_WITH_WEEKDAY_FORMATTER = new Intl.DateTimeFormat(
  "en-US",
  {
    timeZone: NEW_YORK_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  },
);
const NEW_YORK_CLOCK_LABEL_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: NEW_YORK_TIME_ZONE,
  hour: "numeric",
  minute: "2-digit",
});

const EXTENDED_SESSION_LABELS = Object.freeze({
  premarket: "PRE",
  "after-hours": "AFTER",
});

const EXTENDED_SESSION_START_LABELS = Object.freeze({
  premarket: "4:00 AM",
  "after-hours": "CLOSE 4:00 PM",
});

const EXTENDED_SESSION_END_LABELS = Object.freeze({
  premarket: "OPEN 9:30 AM",
  "after-hours": "CLOSE 8:00 PM",
});

function newYorkParts(timestamp, { includeWeekday = false } = {}) {
  const parsed = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return null;

  try {
    const formatter = includeWeekday
      ? NEW_YORK_DATE_TIME_PARTS_WITH_WEEKDAY_FORMATTER
      : NEW_YORK_DATE_TIME_PARTS_FORMATTER;
    const parts = formatter.formatToParts(parsed);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const result = {
      year: Number(values.year),
      month: Number(values.month),
      day: Number(values.day),
      hour: Number(values.hour),
      minute: Number(values.minute),
      second: Number(values.second),
      weekday: values.weekday || null,
    };
    if (
      !Number.isInteger(result.year) ||
      !Number.isInteger(result.month) ||
      !Number.isInteger(result.day) ||
      !Number.isInteger(result.hour) ||
      !Number.isInteger(result.minute) ||
      !Number.isInteger(result.second)
    ) {
      return null;
    }
    return result;
  } catch (error) {
    return null;
  }
}

function calendarDateWithOffset({ year, month, day }, dayOffset = 0) {
  const cursor = new Date(Date.UTC(year, month - 1, day));
  cursor.setUTCDate(cursor.getUTCDate() + dayOffset);
  return {
    year: cursor.getUTCFullYear(),
    month: cursor.getUTCMonth() + 1,
    day: cursor.getUTCDate(),
  };
}

function newYorkOffsetMilliseconds(timestampMs) {
  const roundedTimestamp = Math.floor(timestampMs / 1000) * 1000;
  const parts = newYorkParts(new Date(roundedTimestamp));
  if (!parts) return 0;
  const representedAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  return representedAsUtc - roundedTimestamp;
}

function newYorkWallClockToUtc({ year, month, day, hour = 0, minute = 0, second = 0 }) {
  const wallClockUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  let guess = wallClockUtc;

  // Two passes are sufficient around DST boundaries because the first pass
  // lands close enough for the second pass to use the correct zone offset.
  for (let pass = 0; pass < 3; pass += 1) {
    guess = wallClockUtc - newYorkOffsetMilliseconds(guess);
  }
  return guess;
}

function timelineX(timestampMs, domain, chartWidth) {
  if (!domain || !Number.isFinite(timestampMs)) return null;
  const span = domain.endMs - domain.startMs;
  if (!(span > 0)) return null;
  return ((timestampMs - domain.startMs) / span) * chartWidth;
}

function newYorkClockLabel(timestampMs) {
  try {
    return NEW_YORK_CLOCK_LABEL_FORMATTER.format(new Date(timestampMs));
  } catch (error) {
    return "—";
  }
}

export function classifyNewYorkMarketSession(timestamp) {
  if (!timestamp) return "unknown";
  const parts = newYorkParts(timestamp, { includeWeekday: true });
  if (!parts) return "unknown";
  const minutes = parts.hour * 60 + parts.minute;
  if (parts.weekday === "Sat") return "closed";
  if (parts.weekday === "Sun") {
    return minutes >= 20 * 60 ? "overnight" : "closed";
  }
  if (minutes < 4 * 60) return "overnight";
  if (minutes >= 4 * 60 && minutes < 9 * 60 + 30) return "premarket";
  if (minutes >= 9 * 60 + 30 && minutes < 16 * 60) return "regular";
  if (minutes >= 16 * 60 && minutes < 20 * 60) return "after-hours";
  if (minutes >= 20 * 60 && parts.weekday !== "Fri") return "overnight";
  return "closed";
}

export function barsForSessionVisibility(bars, { showExtendedHours, daily }) {
  if (!Array.isArray(bars)) return [];
  if (daily) return bars;

  return bars.filter((bar) => {
    const session = classifyNewYorkMarketSession(bar?.timestamp);
    if (showExtendedHours) {
      return (
        session === "overnight" ||
        session === "premarket" ||
        session === "regular" ||
        session === "after-hours"
      );
    }
    return session === "regular";
  });
}

export function buildNewYork24HourDomain(timestamp) {
  const parts = newYorkParts(timestamp);
  if (!parts) return null;

  const marketDate =
    parts.hour < 4
      ? calendarDateWithOffset(parts, -1)
      : { year: parts.year, month: parts.month, day: parts.day };
  const nextDate = calendarDateWithOffset(marketDate, 1);
  const startMs = newYorkWallClockToUtc({ ...marketDate, hour: 4 });
  const endMs = newYorkWallClockToUtc({ ...nextDate, hour: 4 });
  if (!(endMs > startMs)) return null;

  return {
    startMs,
    endMs,
    marketDate,
    timeZone: NEW_YORK_TIME_ZONE,
  };
}

export function barsInsideTimelineDomain(bars, domain) {
  if (!Array.isArray(bars) || !domain) return [];
  return bars.filter((bar) => {
    const timestampMs = Date.parse(bar?.timestamp);
    return (
      Number.isFinite(timestampMs) &&
      timestampMs >= domain.startMs &&
      timestampMs < domain.endMs
    );
  });
}

export function buildNewYork24HourAxis(domain, chartWidth = 1000) {
  if (!domain?.marketDate || !(domain.endMs > domain.startMs)) return [];
  const offsets = [0, 4, 8, 12, 16, 20, 24];

  return offsets.map((hourOffset) => {
    const absoluteHour = 4 + hourOffset;
    const dayOffset = Math.floor(absoluteHour / 24);
    const hour = absoluteHour % 24;
    const date = calendarDateWithOffset(domain.marketDate, dayOffset);
    const timestampMs = newYorkWallClockToUtc({ ...date, hour });
    return {
      id: `clock-${hourOffset}`,
      timestampMs,
      x: Math.max(0, Math.min(chartWidth, timelineX(timestampMs, domain, chartWidth))),
      label: newYorkClockLabel(timestampMs),
    };
  });
}

export function buildScheduledExtendedSessionBands(domain, chartWidth = 1000) {
  if (!domain?.marketDate || !(domain.endMs > domain.startMs)) return [];
  const date = domain.marketDate;
  const definitions = [
  {
    session: "premarket",
    label: "PRE",
    startLabel: "4:00 AM",
    endLabel: "OPEN 9:30 AM",
    startHour: 4,
    startMinute: 0,
    endHour: 9,
    endMinute: 30,
  },
  {
    session: "after-hours",
    label: "AFTER",
    startLabel: "CLOSE 4:00 PM",
    endLabel: "CLOSE 8:00 PM",
    startHour: 16,
    startMinute: 0,
    endHour: 20,
    endMinute: 0,
  },
];

  return definitions.map((definition) => {
    const startMs = newYorkWallClockToUtc({
      ...date,
      hour: definition.startHour,
      minute: definition.startMinute,
    });
    const endMs = newYorkWallClockToUtc({
      ...date,
      hour: definition.endHour,
      minute: definition.endMinute,
    });
    const x = Math.max(0, Math.min(chartWidth, timelineX(startMs, domain, chartWidth)));
    const right = Math.max(0, Math.min(chartWidth, timelineX(endMs, domain, chartWidth)));
   return {
  id: `${definition.session}-${startMs}`,
  session: definition.session,
  label: definition.label,
  startLabel: definition.startLabel,
  endLabel: definition.endLabel,
  x,
  width: Math.max(1, right - x),
      };
  });
}

export function buildExtendedSessionBands(candles, chartWidth = 1000) {
  if (!Array.isArray(candles) || candles.length === 0) return [];

  const onscreen = candles.filter(
    (candle) => Number.isFinite(candle?.x) && candle.x >= 0 && candle.x <= chartWidth,
  );
  if (!onscreen.length) return [];

  const slot =
    onscreen.length > 1
      ? Math.max(1, Math.abs(onscreen[1].x - onscreen[0].x))
      : Math.max(1, Number(onscreen[0].bodyWidth) || 8);
  const bands = [];
  let activeBand = null;

  onscreen.forEach((candle) => {
    const session = classifyNewYorkMarketSession(candle.timestamp);
    const label = EXTENDED_SESSION_LABELS[session];
    const startLabel = EXTENDED_SESSION_START_LABELS[session];
    const endLabel = EXTENDED_SESSION_END_LABELS[session];
    if (!label) {
      activeBand = null;
      return;
    }

    const left = Math.max(0, candle.x - slot / 2);
    const right = Math.min(chartWidth, candle.x + slot / 2);
    if (!activeBand || activeBand.session !== session) {
      activeBand = {
        id: `${session}-${candle.timestamp}`,
        session,
        label,
        startLabel,
        endLabel,
        x: left,
        width: Math.max(1, right - left),
      };
      bands.push(activeBand);
      return;
    }

    activeBand.width = Math.max(1, right - activeBand.x);
  });

  return bands;
}
